/**
 * NotificationSnackbar Component
 *
 * Purpose: Displays temporary notification messages using MUI Snackbar with Alert
 * Architecture: Reusable component for error, success, warning, and info notifications
 *
 * Dependencies: React, Material-UI
 * Author: Development Team
 * Created: 2025-11-29
 */

import React from 'react';
import { Snackbar, Alert, AlertColor, SnackbarOrigin } from '@mui/material';

export interface NotificationSnackbarProps {
  /** Whether the snackbar is open */
  open: boolean;
  /** Callback to close the snackbar */
  onClose: () => void;
  /** Message to display */
  message: string;
  /** Severity/type of the notification */
  severity?: AlertColor;
  /** Auto-hide duration in milliseconds (default: 6000, null for no auto-hide) */
  autoHideDuration?: number | null;
  /** Position of the snackbar */
  anchorOrigin?: SnackbarOrigin;
}

/**
 * NotificationSnackbar Component
 *
 * Purpose: Displays temporary notification messages with auto-dismiss
 * Logic: Uses MUI Snackbar with Alert for consistent styling
 * Input: open state, message, severity, onClose handler
 * Output: Snackbar notification at specified position
 */
const NotificationSnackbar: React.FC<NotificationSnackbarProps> = ({
  open,
  onClose,
  message,
  severity = 'info',
  autoHideDuration = 6000,
  anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
}) => {
  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    // Don't close on clickaway
    if (reason === 'clickaway') {
      return;
    }
    onClose();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
    >
      <Alert onClose={handleClose} severity={severity} variant="filled" sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
};

export default NotificationSnackbar;
